import {
  ArrowRight,
  CalendarClock,
  CreditCard,
  Mail,
  Phone,
  Upload,
  Wand2,
} from "lucide-react";
import { useGetList } from "ra-core";
import { Button } from "@/components/ui/button";

import { SectionCard } from "../data-display/SectionCard";
import { TemplatePacksSection } from "../templates/TemplatePacksSection";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

/**
 * Post-signup onboarding (/#/welcome): short on purpose, so a new business
 * reaches the product in minutes. Industry pack first (it makes the empty
 * workspace feel built for the trade), then optional connections, each
 * skippable, then the dashboard. The same cards double as the incomplete-
 * setup checklist reachable any time from Settings.
 */
export const WelcomePage = () => {
  const workspaceId = getActiveWorkspaceId();
  const { data: connections } = useGetList<{
    id: string;
    provider: string;
    status: string;
  }>("integration_connections", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: numbers } = useGetList<{ id: string }>("phone_numbers", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "created_at", order: "DESC" },
  });

  const status = (provider: string) =>
    (connections ?? []).find((c) => c.provider === provider)?.status;

  const steps = [
    {
      key: "email",
      icon: Mail,
      title: "Connect your email",
      description: "Send and receive from your own address, threads included.",
      done:
        status("gmail") === "connected" || status("outlook") === "connected",
    },
    {
      key: "payments",
      icon: CreditCard,
      title: "Accept customer payments",
      description:
        "Connect Stripe so invoices and deposits can be paid online.",
      done: status("stripe_connect") === "connected",
    },
    {
      key: "phone",
      icon: Phone,
      title: "Get a business number",
      description: "Calls and texts in Gravun, on a number that is yours.",
      done: (numbers ?? []).length > 0,
    },
    {
      key: "calendar",
      icon: CalendarClock,
      title: "Connect your calendar",
      description: "Appointments and booking sync both ways.",
      done:
        status("google_calendar") === "connected" ||
        status("microsoft_calendar") === "connected",
    },
  ];

  if (!workspaceId) return null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome to Gravun
        </h1>
        <p className="mt-1 text-[14px] text-muted-foreground">
          Your workspace and 14-day trial are ready. A few optional steps make
          it feel like home; every one of them can wait.
        </p>
      </div>

      <SectionCard title="Set up for your trade">
        <div className="flex flex-col gap-2 px-1">
          <p className="text-[12.5px] text-muted-foreground">
            <Wand2 className="mr-1 inline size-3.5" />
            Install an industry pack: pipelines, templates, checklists and forms
            shaped for your kind of work.
          </p>
          <TemplatePacksSection />
        </div>
      </SectionCard>

      <SectionCard title="Connect your services (all optional)">
        <div className="flex flex-col gap-1 px-1">
          {steps.map((step) => (
            <div
              key={step.key}
              className="flex items-center gap-3 border-b border-border-subtle py-2.5 last:border-0"
            >
              <step.icon className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium">{step.title}</p>
                <p className="text-[12px] text-muted-foreground">
                  {step.description}
                </p>
              </div>
              {step.done ? (
                <span className="text-[12px] font-medium text-success">
                  Done
                </span>
              ) : (
                <Button size="sm" variant="outline" asChild>
                  <a href="#/connections">Set up</a>
                </Button>
              )}
            </div>
          ))}
          <p className="pt-1 text-[11.5px] text-muted-foreground">
            Skip anything; you can finish later under Settings, Connections.
          </p>
        </div>
      </SectionCard>

      <SectionCard title="Bring your data">
        <div className="flex items-center justify-between gap-3 px-1">
          <p className="text-[12.5px] text-muted-foreground">
            <Upload className="mr-1 inline size-3.5" />
            Import contacts from a CSV or Excel export of your old system.
          </p>
          <Button size="sm" variant="outline" asChild>
            <a href="#/contacts">Import contacts</a>
          </Button>
        </div>
      </SectionCard>

      <Button size="lg" asChild>
        <a href="#/">
          Go to my dashboard
          <ArrowRight className="ml-1.5 size-4" />
        </a>
      </Button>
    </div>
  );
};

WelcomePage.path = "/welcome";
